const solutions = [
  {
    id: 'commercial',
    badge: 'Commercial Scale',
    badgeColor: 'bg-secondary-100 text-secondary-700',
    title: 'Commercial Wind Farms',
    subtitle: 'Large-scale wind energy for utilities and major off-takers',
    description: "Commercial wind farms are the backbone of Australia's renewable energy transition. Projects from 10 MW to 1,000 MW+ are being developed across VIC, SA, WA, NSW and QLD, providing long-term, low-cost electricity to grid operators, corporate off-takers, and state governments.",
    image: 'https://readdy.ai/api/search-image?query=Large-scale%20commercial%20wind%20farm%20with%20dozens%20of%20tall%20white%20wind%20turbines%20across%20open%20Australian%20agricultural%20land%2C%20blue%20sky%20with%20light%20clouds%2C%20vast%20scale%2C%20epic%20landscape%20photography%2C%20golden%20fields%2C%20stunning%20renewable%20energy%20infrastructure&width=800&height=560&seq=wind-commercial-au-2026&orientation=landscape',
    features: [
      'Projects 10 MW to 1,000 MW+',
      'Grid connection and network studies',
      'Power Purchase Agreements (PPAs)',
      'AEMO market registration support',
      'Environmental & planning approvals',
      'Community engagement programs',
    ],
    highlight: 'PPAs from 10+ years',
    highlightColor: 'text-secondary-600',
    cta: 'Explore Commercial Wind Projects',
  },
  {
    id: 'industrial',
    badge: 'Industrial & C&I',
    badgeColor: 'bg-primary-100 text-primary-700',
    title: 'Industrial Wind Energy',
    subtitle: 'Behind-the-meter and PPA solutions for large energy users',
    description: 'Australian manufacturers, miners, data centres, and agribusinesses are increasingly accessing wind energy through Corporate Power Purchase Agreements (CPPAs). These long-term contracts fix energy costs, provide Renewable Energy Certificates (RECs), and support scope 2 emissions reduction — without on-site installation.',
    image: 'https://readdy.ai/api/search-image?query=Industrial%20Australian%20mining%20or%20manufacturing%20facility%20with%20wind%20turbines%20visible%20on%20the%20horizon%2C%20heavy%20industry%20infrastructure%2C%20clean%20energy%20integration%2C%20professional%20industrial%20photography%2C%20wide%20landscape%2C%20dramatic%20sky&width=800&height=560&seq=wind-industrial-au-2026&orientation=landscape',
    features: [
      'Corporate PPAs from 1 MW to 200 MW',
      'Renewable Energy Certificates (LGCs)',
      'Scope 2 & scope 3 emissions reduction',
      'Fixed-price contracts 7–25 years',
      'No capex required for off-site PPAs',
      'Dedicated energy procurement advisory',
    ],
    highlight: 'No on-site installation needed',
    highlightColor: 'text-primary-600',
    cta: 'Explore Industrial Wind PPAs',
  },
  {
    id: 'small-wind',
    badge: 'Small Wind',
    badgeColor: 'bg-accent-100 text-accent-800',
    title: 'Small Wind Turbines',
    subtitle: 'Wind energy for farms, remote properties, and microgrids',
    description: 'Small wind turbines (1 kW – 300 kW) are well-suited to Australian farms, remote stations, off-grid communities, and rural businesses. Combined with solar and battery storage in a microgrid configuration, small wind turbines can eliminate diesel generation entirely in many remote locations.',
    image: 'https://readdy.ai/api/search-image?query=Small%20wind%20turbine%20standing%20next%20to%20a%20rural%20Australian%20farmhouse%20or%20station%20homestead%2C%20solar%20panels%20also%20visible%2C%20outback%20or%20rural%20landscape%2C%20blue%20sky%2C%20clean%20lifestyle%20photography%2C%20energy%20independence%20concept&width=800&height=560&seq=wind-small-au-2026&orientation=landscape',
    features: [
      'Turbines from 1 kW to 300 kW',
      'On-grid, off-grid and hybrid systems',
      'Compatible with solar + battery microgrids',
      'Remote area power systems (RAPS)',
      'ARENA grant funding available',
      'Minimal land footprint required',
    ],
    highlight: 'Eliminate diesel dependency',
    highlightColor: 'text-accent-800',
    cta: 'Explore Small Wind Solutions',
  },
];

export default function WindSolutions() {
  return (
    <section className="bg-background-100/50 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-50 border border-accent-200/50 mb-5">
            <i className="ri-stack-line text-accent-700 text-sm"></i>
            <span className="text-xs font-semibold text-accent-800 tracking-wide uppercase whitespace-nowrap">Wind Solutions</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4">
            Wind Energy Solutions<br className="hidden md:block" /> at Every Scale
          </h2>
          <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            Whether you need large-scale grid-connected wind, a corporate PPA, or a small turbine for a remote property — AK Energy connects you with the right wind energy provider.
          </p>
        </div>

        <div className="space-y-10 md:space-y-14">
          {solutions.map((sol, idx) => (
            <article
              key={sol.id}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${idx % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}
            >
              {/* Image */}
              <div className={`${idx % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className="relative rounded-2xl overflow-hidden bg-background-200">
                  <img
                    src={sol.image}
                    alt={`${sol.title} in Australia — AK Energy`}
                    className="w-full h-72 md:h-80 object-cover object-top"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${sol.badgeColor}`}>
                      {sol.badge}
                    </span>
                  </div>
                  <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl px-3 py-2">
                    <span className={`text-sm font-bold ${sol.highlightColor}`}>{sol.highlight}</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={`${idx % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 mb-2">{sol.title}</h3>
                <p className="text-sm font-semibold text-secondary-600 mb-4">{sol.subtitle}</p>
                <p className="text-base text-foreground-600 leading-relaxed mb-6">{sol.description}</p>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-7">
                  {sol.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-foreground-700">
                      <i className="ri-check-line text-primary-500 text-base mt-0.5 shrink-0"></i>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="/#contact"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-xl bg-foreground-950 text-white hover:bg-foreground-800 transition-all duration-200 cursor-pointer"
                >
                  {sol.cta}
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}